import { Button, ButtonVariantEnum, TableList } from '@Wonder-Cave/ui';
import { Filter } from '@Wonder-Cave/ui/dist/Table/Filters';
import { SystemNumberStatusEnum } from '@shared/enums/system-number-status-enum';
import { FilterDataTypeEnum, FilterOperatorEnum, ISearchClientsRecord, ISearchTcrCampaignsRecord } from '@shared/models';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotifications } from '../../../../contexts/NotificationContext';
import useClientLocalSystemNumbers, { IClientLocalSystemNumberRecord } from '../../../../hooks/useClientLocalSystemNumbers';
import { IDropdownValue } from '../../../shared/Form/Dropdown';
import GenericDropdown from '../../../shared/Form/Dropdowns/GenericDropdown';
import DeactivateNumbersBulkModal, { IBulkDeactivateReviewSummary } from './DeactivateNumbersBulkModal';
import DeactivateNumbersBulkReviewModal from './DeactivateNumbersBulkReviewModal';
import { buildColumns } from './number-management.columns';
import { getAreaCode } from './number-management.helpers';
import {
  handleBulkDeactivateConfirm,
  handleDeactivate,
  handlePurchaseConfirm,
} from './number-management.handlers';
import PurchaseNumbersModal, { IPurchaseReviewSummary } from './PurchaseNumbersModal';
import PurchaseNumbersReviewModal from './PurchaseNumbersReviewModal';

interface NumberManagementProps {
  client?: ISearchClientsRecord;
  clientLoading?: boolean;
  allTcrCampaigns?: ISearchTcrCampaignsRecord[];
  tcrCampaignsLoading?: boolean;
}

const NUMBER_MANAGEMENT_PAGE_SIZE = 10;

const uniqueDropdownOptions = (values: (string | null | undefined)[]): IDropdownValue[] => {
  const seen = new Set<string>();
  const options: IDropdownValue[] = [];
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ label: value, value });
  }
  return options.sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

export const NumberManagement = ({ client, clientLoading, allTcrCampaigns = [], tcrCampaignsLoading = false }: NumberManagementProps) => {
  const { clientId } = useParams();
  const { addNotification } = useNotifications();

  const [{ data, loading: numbersLoading }, refetch] = useClientLocalSystemNumbers({
    clientId,
    environment: client?.environment,
  });

  const loading = clientLoading || numbersLoading || !client?.environment;

  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [page, setPage] = useState(0);
  const [deactivatingIds, setDeactivatingIds] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [reviewSummary, setReviewSummary] = useState<IBulkDeactivateReviewSummary | null>(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseReviewSummary, setPurchaseReviewSummary] = useState<IPurchaseReviewSummary | null>(null);
  const [isPurchaseSubmitting, setIsPurchaseSubmitting] = useState(false);
  const [purchaseFormResetKey, setPurchaseFormResetKey] = useState(0);
  const [isPostPurchase, setIsPostPurchase] = useState(false);

  const allRecords: IClientLocalSystemNumberRecord[] = data?.records ?? [];

  const tcrCampaignOptions = useMemo(() => uniqueDropdownOptions(allRecords.map(r => r.tcrCampaignId)), [allRecords]);
  const statusOptions = useMemo(() => Object.values(SystemNumberStatusEnum).map(s => ({ label: s, value: s })), []);
  const providerOptions = useMemo(() => uniqueDropdownOptions(allRecords.map(r => r.providerName)), [allRecords]);
  const areaCodeOptions = useMemo(() => uniqueDropdownOptions(allRecords.map(r => getAreaCode(r.phoneNumber))), [allRecords]);

  const filteredRecords = useMemo(() => {
    return allRecords.filter(record => {
      return activeFilters.every(filter => {
        const selected = filter.selectedOption?.value;
        if (selected === undefined || selected === null || selected === '') return true;

        switch (filter.id) {
          case 'tcrCampaignId': return record.tcrCampaignId === selected;
          case 'status': return record.status === selected;
          case 'providerName': return record.providerName === selected;
          case 'areaCode': return getAreaCode(record.phoneNumber) === selected;
          default: return true;
        }
      });
    });
  }, [allRecords, activeFilters]);

  const pagedRecords = useMemo(() => {
    const start = page * NUMBER_MANAGEMENT_PAGE_SIZE;
    return filteredRecords.slice(start, start + NUMBER_MANAGEMENT_PAGE_SIZE);
  }, [filteredRecords, page]);

  const closeBulkModals = () => {
    setShowBulkModal(false);
    setReviewSummary(null);
  };

  const closePurchaseModals = () => {
    setShowPurchaseModal(false);
    setPurchaseReviewSummary(null);
    setPurchaseFormResetKey(k => k + 1);
    setIsPostPurchase(false);
  };

  const hasNoSendingNumbers = !loading && allRecords.length === 0;
  const filtersYieldedNoResults = !loading && allRecords.length > 0 && filteredRecords.length === 0;

  const columns = useMemo(() => buildColumns(deactivatingIds), [deactivatingIds]);

  return <div className='flex flex-col'>
    <div className='flex items-center justify-between pl-20 pr-32 mb-4'>
      <h2>Number Management</h2>
      <div className='flex items-center gap-4'>
        <Button
          variant={ButtonVariantEnum.PRIMARY}
          onClick={() => setShowPurchaseModal(true)}
          disabled={loading}
        >
          PURCHASE NUMBERS
        </Button>
        <Button
          variant={ButtonVariantEnum.TERTIARY}
          onClick={() => setShowBulkModal(true)}
          disabled={loading || allRecords.length === 0}
        >
          DEACTIVATE NUMBERS
        </Button>
      </div>
    </div>
    <div className='-ml-12'>
      <TableList
        loading={loading}
        shimmer
        tableHeight='max-h-[60vh]'
        columns={columns}
        items={pagedRecords}
        totalCount={filteredRecords.length}
        limit={NUMBER_MANAGEMENT_PAGE_SIZE}
        paginate
        onPaginate={setPage}
        actions={[
          {
            label: 'Deactivate',
            action: (item: IClientLocalSystemNumberRecord) =>
              handleDeactivate(item, { client, addNotification, setDeactivatingIds, refetch }),
            hidden: (item: IClientLocalSystemNumberRecord) =>
              !item?.tcrCampaignId
              || deactivatingIds.includes(item?.id)
              || item?.status === SystemNumberStatusEnum.DEACTIVATED
              || item?.status === SystemNumberStatusEnum.DEACTIVATING,
          }
        ]}
        cssOverride={{
          'table-list': '!px-0',
          'table-list-header': '!pl-4',
        }}
        filters={[
          {
            label: 'TCR Campaign',
            id: 'tcrCampaignId',
            dataType: FilterDataTypeEnum.STRING,
            operator: FilterOperatorEnum.EQUALS,
            customDropdown: (value, onChange) => <GenericDropdown
              placeholder='Select a TCR campaign'
              label=''
              options={tcrCampaignOptions}
              value={value}
              onChange={(newValue) => onChange(newValue as IDropdownValue)}
            />,
          },
          {
            label: 'Status',
            id: 'status',
            dataType: FilterDataTypeEnum.STRING,
            operator: FilterOperatorEnum.EQUALS,
            customDropdown: (value, onChange) => <GenericDropdown
              placeholder='Select a status'
              label=''
              options={statusOptions}
              value={value}
              onChange={(newValue) => onChange(newValue as IDropdownValue)}
            />,
          },
          {
            label: 'Provider',
            id: 'providerName',
            dataType: FilterDataTypeEnum.STRING,
            operator: FilterOperatorEnum.EQUALS,
            customDropdown: (value, onChange) => <GenericDropdown
              placeholder='Select a provider'
              label=''
              options={providerOptions}
              value={value}
              onChange={(newValue) => onChange(newValue as IDropdownValue)}
            />,
          },
          {
            label: 'Area Code',
            id: 'areaCode',
            dataType: FilterDataTypeEnum.STRING,
            operator: FilterOperatorEnum.EQUALS,
            customDropdown: (value, onChange) => <GenericDropdown
              placeholder='Select an area code'
              label=''
              options={areaCodeOptions}
              value={value}
              onChange={(newValue) => onChange(newValue as IDropdownValue)}
            />,
          },
        ]}
        onFilter={(filters: Filter[]) => {
          setActiveFilters(filters);
          setPage(0);
        }}
      />

      {hasNoSendingNumbers && (
        <div className='py-12 text-center text-medium-gray'>
          This client has no sending numbers.
        </div>
      )}

      {filtersYieldedNoResults && (
        <div className='py-12 text-center text-medium-gray'>
          No numbers match the applied filters.
        </div>
      )}
    </div>

    <DeactivateNumbersBulkModal
      show={showBulkModal}
      records={allRecords}
      allTcrCampaigns={allTcrCampaigns}
      tcrCampaignsLoading={tcrCampaignsLoading}
      onClose={closeBulkModals}
      onSubmit={(summary: IBulkDeactivateReviewSummary) => {
        setReviewSummary(summary);
        setShowBulkModal(false);
      }}
    />

    <DeactivateNumbersBulkReviewModal
      show={reviewSummary !== null}
      isLoading={isBulkSubmitting}
      summary={reviewSummary}
      onCancel={closeBulkModals}
      onConfirm={() =>
        handleBulkDeactivateConfirm({
          client,
          reviewSummary,
          addNotification,
          setIsBulkSubmitting,
          closeBulkModals,
          refetch,
        })
      }
    />

    <PurchaseNumbersModal
      show={showPurchaseModal && purchaseReviewSummary === null}
      records={allRecords}
      allTcrCampaigns={allTcrCampaigns}
      tcrCampaignsLoading={tcrCampaignsLoading}
      client={client}
      onClose={closePurchaseModals}
      onReview={(summary: IPurchaseReviewSummary) => {
        setPurchaseReviewSummary(summary);
      }}
      onConfirm={(summary: IPurchaseReviewSummary) =>
        handlePurchaseConfirm(summary, {
          client,
          addNotification,
          setIsPurchaseSubmitting,
          setIsPostPurchase,
          setPurchaseReviewSummary,
          setShowPurchaseModal,
          refetch,
        })
      }
      isConfirming={isPurchaseSubmitting}
      resetKey={purchaseFormResetKey}
    />

    <PurchaseNumbersReviewModal
      show={purchaseReviewSummary !== null}
      summary={purchaseReviewSummary}
      isPostPurchase={isPostPurchase}
      onBack={isPostPurchase ? closePurchaseModals : () => {
        setPurchaseReviewSummary(null);
        setShowPurchaseModal(true);
      }}
    />
  </div>;
};
